import React, { useMemo } from 'react';
import { nomeParaCena } from 'features/comercial/cenas/eventosDoRanking';

/**
 * A régua de um recebimento: do vencimento ao pagamento, com a OS de cobrança que estava aberta e
 * quem a finalizou — que é quem leva a comissão.
 *
 * O eixo é proporcional ao tempo. A faixa colorida é a janela interna (OS de cobrança aberta); o
 * pagamento só comissiona se cair dentro dela e se o título já estava vencido quando ela abriu.
 */

const STATUS = {
  A: 'aberta', AN: 'em análise', EN: 'encaminhada', AS: 'assumida', AG: 'agendada',
  DS: 'em deslocamento', EX: 'em execução', F: 'finalizada', RAG: 'aguardando reagendamento',
};
const ASSUNTO = { 594: 'Cobrança interna', 595: 'Aguardar pagamento', 596: 'Cobrança externa', 597: 'Cobrança externa' };
const DIA_MS = 86400000;

// 'YYYY-MM-DD' e 'YYYY-MM-DD HH:MM:SS' no fuso local: `new Date('2026-09-01')` seria meia-noite UTC.
export const quando = (texto) => {
  if (!texto) return null;
  const s = String(texto);
  return new Date(s.length === 10 ? `${s}T00:00:00` : s.replace(' ', 'T'));
};
// O accent é a cor da empresa, em hex dinâmico: a opacidade do Tailwind não se aplica a `var()`.
const LAVAGEM = { backgroundColor: 'color-mix(in srgb, var(--accent) 16%, transparent)' };
const curto = (data) => data.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
// O pagamento vem com hora da baixa; sem ela (meia-noite), só o dia.
const hora = (data) => (data.getHours() || data.getMinutes() ? `${curto(data)} ${data.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}` : curto(data));
const comHora = (data) => `${data.toLocaleDateString('pt-BR')}${data.getHours() || data.getMinutes() ? ` às ${data.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}` : ''}`;
const reais = (valor) => Number(valor || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const pessoa = (nome) => (nome ? nomeParaCena(nome) : 'sem técnico');

/** Mensagens seguidas com o mesmo status e a mesma pessoa são um passo só na régua. */
export const passosDaOs = (historico) => historico.reduce((passos, m) => {
  const anterior = passos[passos.length - 1];
  if (anterior && anterior.id_os === m.id_os && anterior.status === m.status && anterior.tecnico_id === m.tecnico_id) return passos;
  return [...passos, m];
}, []);

export const eventosDaRegua = (linha, historico = []) => {
  const eventos = [];
  const vencimento = quando(linha.vencimento);
  if (vencimento) {
    eventos.push({
      chave: 'vencimento', tipo: 'marco', data: vencimento,
      titulo: linha.tipo === 'renegociacao' ? 'Vencimento mais antigo renegociado' : 'Boleto venceu',
      detalhe: linha.tipo === 'renegociacao' ? `Renegociação ${linha.renegociacao_id}` : `Boleto ${linha.boleto_id}`,
    });
  }
  passosDaOs(historico).forEach((m, i) => {
    const assunto = ASSUNTO[m.assunto] || 'OS';
    const status = STATUS[m.status] || m.status;
    const externa = m.assunto === 596 || m.assunto === 597;
    eventos.push({
      chave: `os-${m.id_os}-${i}`, tipo: externa ? 'externa' : 'os', data: quando(m.data),
      titulo: m.status === 'A' ? `${assunto} aberta (OS ${m.id_os})` : `${assunto} ${status}${m.tecnico ? ` · ${pessoa(m.tecnico)}` : ''}`,
      detalhe: m.status === 'F' && m.id_os === linha.os_id && m.tecnico_id === linha.finalizador_id ? 'Quem finaliza a OS leva a comissão' : `OS ${m.id_os}`,
    });
  });
  const pagou = quando(linha.baixa || linha.pagamento);
  if (pagou) {
    eventos.push({
      chave: 'pagamento', tipo: 'pagamento', data: pagou, titulo: 'Cliente pagou',
      detalhe: `${reais(linha.valor_pago)} com ${linha.dias_atraso} dias de atraso`,
    });
  }
  const finalizou = quando(linha.finalizacao);
  if (linha.gerou && finalizou) {
    eventos.push({
      chave: 'comissao', tipo: 'comissao', data: finalizou,
      titulo: `Comissão de ${reais(linha.comissao)} para ${pessoa(linha.finalizador)}`, detalhe: linha.motivo,
    });
  }

  return eventos.filter((e) => e.data && !Number.isNaN(e.data.getTime())).sort((a, b) => a.data - b.data);
};

const COR = {
  marco: 'bg-[color:var(--text-muted)]',
  os: 'bg-[color:var(--accent)]',
  externa: 'bg-[color:var(--status-danger-fg)]',
  pagamento: 'bg-[color:var(--text-primary)]',
  comissao: 'bg-[color:var(--status-good-fg)]',
};

/**
 * Escala quebrada. O atraso dura semanas e a OS de cobrança, horas: num eixo linear a abertura da
 * OS, o pagamento e a finalização caem todos no mesmo ponto. Com a janela da OS dentro da régua, o
 * atraso fica comprimido nos primeiros 28% e a janela ganha o resto, depois de uma quebra.
 */
export const escalaDaRegua = (inicio, janela, fim) => {
  const quebra = Boolean(janela) && janela > inicio && janela < fim;
  const linear = (t, de, ate, x0, x1) => x0 + ((t - de) / Math.max(1, ate - de)) * (x1 - x0);
  return {
    quebra: quebra ? 30 : null,
    x: (data) => {
      const t = data.getTime();
      if (!quebra) return linear(t, inicio, fim, 0, 100);
      return t < janela ? linear(t, inicio, janela, 0, 28) : linear(t, janela, fim, 32, 100);
    },
  };
};

const ReguaDaCobranca = ({ linha, historico, carregando }) => {
  const eventos = useMemo(() => eventosDaRegua(linha, historico || []), [linha, historico]);
  if (eventos.length === 0) return null;

  const inicio = eventos[0].data.getTime();
  const fim = Math.max(eventos[eventos.length - 1].data.getTime(), inicio + DIA_MS);
  const janelaDe = quando(linha.inicio_janela);
  const janelaAte = quando(linha.finalizacao) || quando(linha.baixa);
  const escala = escalaDaRegua(inicio, janelaDe?.getTime(), fim);
  const pos = (data) => `${escala.x(data)}%`;
  const pagamento = eventos.find((e) => e.tipo === 'pagamento');
  const comissao = eventos.find((e) => e.tipo === 'comissao');
  const dias = Math.round((fim - inicio) / DIA_MS);

  return (
    <div className="space-y-5">
      <figure aria-label={`Régua de ${dias} dias, do ${eventos[0].titulo.toLowerCase()} ao último evento`}>
        <div className="relative mx-3 h-28" aria-hidden="true">
          <div className="absolute inset-x-0 top-14 h-px bg-[color:var(--border)]" />
          {janelaDe && janelaAte && (
            <div
              className="absolute top-12 h-[18px] rounded-full border border-[color:var(--accent)]"
              style={{ ...LAVAGEM, left: pos(janelaDe), width: `max(6px, calc(${pos(janelaAte)} - ${pos(janelaDe)}))` }}
            />
          )}
          {escala.quebra && (
            <span className="absolute top-[50px] h-3 w-2 -translate-x-1/2 skew-x-[-20deg] border-x border-[color:var(--text-muted)] bg-[color:var(--surface-strong)]" style={{ left: `${escala.quebra}%` }} />
          )}
          {eventos.map((e) => (
            <span key={e.chave} className={`absolute top-14 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full ring-2 ring-[color:var(--surface-strong)] ${COR[e.tipo]}`} style={{ left: pos(e.data) }} />
          ))}
          {/* Os três marcos que respondem à pergunta: quando venceu, quando pagou, quem comissionou. */}
          <Rotulo x={escala.x(eventos[0].data)} lado="baixo" texto={curto(eventos[0].data)} legenda="Venceu" />
          {pagamento && <Rotulo x={escala.x(pagamento.data)} lado="cima" texto={hora(pagamento.data)} legenda="Pagou" forte />}
          {comissao && <Rotulo x={escala.x(comissao.data)} lado="baixo" texto={pessoa(linha.finalizador).split(' ')[0]} legenda="Comissionou" bom />}
        </div>
        <figcaption className="flex flex-wrap gap-x-4 gap-y-1 px-3 text-xs text-[color:var(--text-muted)]">
          {janelaDe && <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-5 rounded-full border border-[color:var(--accent)]" style={LAVAGEM} />OS de cobrança aberta</span>}
          {eventos.some((e) => e.tipo === 'externa') && <span className="inline-flex items-center gap-1.5"><span className={`h-2.5 w-2.5 rounded-full ${COR.externa}`} />Cobrança externa</span>}
          <span>{escala.quebra ? `Atraso de ${dias} ${dias === 1 ? 'dia' : 'dias'} comprimido; a janela da OS aparece ampliada` : `${dias} ${dias === 1 ? 'dia' : 'dias'} do vencimento ao fim`}</span>
        </figcaption>
      </figure>

      <ol className="relative space-y-3 border-l border-[color:var(--border)] pl-5">
        {eventos.map((e) => (
          <li key={e.chave} className="relative">
            <span className={`absolute -left-[26px] top-1 h-2.5 w-2.5 rounded-full ring-2 ring-[color:var(--surface-strong)] ${COR[e.tipo]}`} aria-hidden="true" />
            <p className={`text-sm ${e.tipo === 'pagamento' || e.tipo === 'comissao' ? 'font-semibold' : 'font-medium'} ${e.tipo === 'comissao' ? 'text-[color:var(--status-good-strong)]' : 'text-[color:var(--text-primary)]'}`}>{e.titulo}</p>
            <p className="text-xs text-[color:var(--text-muted)]"><time dateTime={e.data.toISOString()}>{comHora(e.data)}</time> · {e.detalhe}</p>
          </li>
        ))}
        {carregando && <li className="text-xs text-[color:var(--text-muted)]">Carregando o histórico da OS…</li>}
      </ol>
    </div>
  );
};

// Perto das bordas o rótulo se alinha para dentro, em vez de sair da régua.
const Rotulo = ({ x, lado, texto, legenda, forte, bom }) => {
  const deslocamento = x < 12 ? 'translate-x-0 text-left' : x > 88 ? '-translate-x-full text-right' : '-translate-x-1/2 text-center';
  const cor = bom ? 'text-[color:var(--status-good-strong)]' : 'text-[color:var(--text-primary)]';
  return (
    <div className={`absolute ${lado === 'cima' ? 'top-0' : 'top-[68px]'} ${deslocamento} whitespace-nowrap leading-tight`} style={{ left: `${x}%` }}>
      <span className="block text-[11px] text-[color:var(--text-muted)]">{legenda}</span>
      <span className={`block text-xs ${forte || bom ? 'font-semibold' : 'font-medium'} ${cor}`}>{texto}</span>
    </div>
  );
};

export default ReguaDaCobranca;
